import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  subscriptionPlans: [],
  currentSubscriptionPlan: null,
  isLoading: false,
  error: null,
  filters: {
    search: '',
    isActive: null,
  },
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  },
};

const subscriptionSlice = createSlice({
  name: 'subscription',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentSubscriptionPlan: (state) => {
      state.currentSubscriptionPlan = null;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    setPage: (state, action) => {
      state.pagination.page = action.payload;
    },
    setLimit: (state, action) => {
      state.pagination.limit = action.payload;
    },
    setCurrentPage: (state, action) => {
      state.pagination.page = action.payload;
    },
    setItemsPerPage: (state, action) => {
      state.pagination.limit = action.payload;
      state.pagination.page = 1; // Reset to first page when changing page size
    },
    resetPagination: (state) => {
      state.pagination = {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
        hasNext: false,
        hasPrev: false,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // Get Subscription Plans
      .addCase('subscription/getSubscriptionPlans/pending', (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase('subscription/getSubscriptionPlans/fulfilled', (state, action) => {
        state.isLoading = false;
        
        // Handle both array response and paginated response
        if (Array.isArray(action.payload)) {
          state.subscriptionPlans = action.payload;
        } else if (action.payload.data) {
          // Paginated response
          state.subscriptionPlans = action.payload.data;
          state.pagination = {
            page: action.payload.page || state.pagination.page,
            limit: action.payload.limit || state.pagination.limit,
            total: action.payload.total || 0,
            totalPages: action.payload.totalPages || 0,
            hasNext: action.payload.hasNext || false,
            hasPrev: action.payload.hasPrev || false,
          };
        } else {
          state.subscriptionPlans = action.payload;
        }
        
        state.error = null;
      })
      .addCase('subscription/getSubscriptionPlans/rejected', (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch subscription plans';
      })
      // Get Subscription Plan by ID
      .addCase('subscription/getSubscriptionPlanById/pending', (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase('subscription/getSubscriptionPlanById/fulfilled', (state, action) => {
        state.isLoading = false;
        state.currentSubscriptionPlan = action.payload;
        state.error = null;
      })
      .addCase('subscription/getSubscriptionPlanById/rejected', (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch subscription plan';
      })
      // Create Subscription Plan
      .addCase('subscription/createSubscriptionPlan/pending', (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase('subscription/createSubscriptionPlan/fulfilled', (state, action) => {
        state.isLoading = false;
        state.subscriptionPlans.unshift(action.payload); // Add new plan to the beginning
        state.error = null;
      })
      .addCase('subscription/createSubscriptionPlan/rejected', (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to create subscription plan';
      })
      // Update Subscription Plan
      .addCase('subscription/updateSubscriptionPlan/pending', (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase('subscription/updateSubscriptionPlan/fulfilled', (state, action) => {
        state.isLoading = false;
        const index = state.subscriptionPlans.findIndex(plan => plan._id === action.payload._id);
        if (index !== -1) {
          state.subscriptionPlans[index] = action.payload;
        }
        state.error = null;
      })
      .addCase('subscription/updateSubscriptionPlan/rejected', (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to update subscription plan';
      })
      // Delete Subscription Plan
      .addCase('subscription/deleteSubscriptionPlan/pending', (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase('subscription/deleteSubscriptionPlan/fulfilled', (state, action) => {
        state.isLoading = false;
        state.subscriptionPlans = state.subscriptionPlans.filter(plan => plan._id !== action.payload);
        state.error = null;
      })
      .addCase('subscription/deleteSubscriptionPlan/rejected', (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to delete subscription plan';
      });
  },
});

export const { 
  clearError, 
  clearCurrentSubscriptionPlan, 
  setFilters, 
  setPagination, 
  setPage, 
  setLimit, 
  setCurrentPage, 
  setItemsPerPage, 
  resetPagination 
} = subscriptionSlice.actions;

export default subscriptionSlice.reducer;
